"use client";

import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useEffectEvent,
} from "react";
import { PlayCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  VideoPlayer,
  VideoPlayerControlBar,
  VideoPlayerPlayButton,
  VideoPlayerSeekBackwardButton,
  VideoPlayerSeekForwardButton,
  VideoPlayerTimeDisplay,
  VideoPlayerTimeRange,
  VideoPlayerMuteButton,
  VideoPlayerVolumeRange,
  VideoPlayerFullscreenButton,
} from "@/components/kibo-ui/video-player";
import { VideoPlayerSettings } from "./VideoPlayerSettings";

interface QualityLevel {
  height: number;
  bitrate: number;
  index: number;
}

export interface HlsVideoPlayerActions {
  replay: () => void;
  unmuteAndRestart: () => void;
}

interface HlsVideoPlayerProps {
  src: string;
  className?: string;
  poster?: string;
  onPlay?: () => void;
  onEnded?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  actionsRef?: React.MutableRefObject<HlsVideoPlayerActions | null>;
  /** Start playback muted as soon as the media is ready */
  autoPlayMuted?: boolean;
  /** Called when muted autoplay was rejected by the browser */
  onAutoplayBlocked?: () => void;
  /** Disable media-chrome keyboard hotkeys (space/arrows) so page scrolling is never captured */
  noHotkeys?: boolean;
  /** media-chrome autohide seconds; -1 keeps controls always visible */
  autoHide?: number;
  children?: React.ReactNode;
}

const HlsVideoPlayer: React.FC<HlsVideoPlayerProps> = ({
  src,
  className,
  poster,
  onPlay,
  onEnded,
  onTimeUpdate,
  actionsRef,
  autoPlayMuted,
  onAutoplayBlocked,
  noHotkeys,
  autoHide,
  children,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<import("hls.js").default | null>(null);
  const bufferingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [buffering, setBuffering] = useState(false);
  const [showBuffering, setShowBuffering] = useState(false);
  const [error, setError] = useState<string>("");
  const [playbackRate, setPlaybackRate] = useState(1);
  const [qualityLevels, setQualityLevels] = useState<QualityLevel[]>([]);
  const [currentQuality, setCurrentQuality] = useState(-1);

  const isLoading = initialLoading || showBuffering;

  useEffect(() => {
    if (buffering) {
      bufferingTimeoutRef.current = setTimeout(() => {
        setShowBuffering(true);
      }, 200);
    } else {
      if (bufferingTimeoutRef.current) {
        clearTimeout(bufferingTimeoutRef.current);
        bufferingTimeoutRef.current = null;
      }
      setShowBuffering(false);
    }

    return () => {
      if (bufferingTimeoutRef.current) {
        clearTimeout(bufferingTimeoutRef.current);
      }
    };
  }, [buffering]);

  const handleEnded = useEffectEvent(() => {
    onEnded?.();
  });

  const handlePlay = useEffectEvent(() => {
    onPlay?.();
  });

  const handleAutoplayBlocked = useEffectEvent(() => {
    onAutoplayBlocked?.();
  });

  const handleTimeUpdate = useEffectEvent(() => {
    const video = videoRef.current;
    if (
      !video ||
      !onTimeUpdate ||
      !Number.isFinite(video.duration) ||
      video.duration <= 0
    ) {
      return;
    }
    onTimeUpdate(video.currentTime, video.duration);
  });

  useEffect(() => {
    if (!actionsRef) return;
    actionsRef.current = {
      replay: () => {
        const video = videoRef.current;
        if (!video) return;
        video.currentTime = 0;
        void video.play();
      },
      unmuteAndRestart: () => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = false;
        video.currentTime = 0;
        void video.play();
      },
    };
    return () => {
      actionsRef.current = null;
    };
  }, [actionsRef]);

  const handlePlaybackRateChange = useCallback((rate: number) => {
    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
  }, []);

  const handleQualityChange = useCallback((index: number) => {
    setCurrentQuality(index);
    if (hlsRef.current) {
      hlsRef.current.currentLevel = index;
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    let cancelled = false;
    let nativeMetadataHandler: (() => void) | null = null;
    let nativeErrorHandler: (() => void) | null = null;

    const loadHls = async () => {
      setInitialLoading(true);
      setBuffering(false);
      setError("");
      setQualityLevels([]);
      setCurrentQuality(-1);

      try {
        const Hls = (await import("hls.js")).default;
        if (cancelled) return;

        // Check HLS.js support FIRST (works on Safari too, and gives us quality selection)
        if (Hls.isSupported()) {
          // HLS.js supported - continue below
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
          // Fallback to native HLS only if HLS.js not supported
          video.src = src;
          nativeMetadataHandler = () => setInitialLoading(false);
          nativeErrorHandler = () => setError("Failed to load video");
          video.addEventListener("loadedmetadata", nativeMetadataHandler);
          video.addEventListener("error", nativeErrorHandler);
          return;
        } else {
          setError("HLS is not supported in this browser");
          setInitialLoading(false);
          return;
        }

        if (hlsRef.current) {
          hlsRef.current.destroy();
        }

        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 90,
        });

        hlsRef.current = hls;

        hls.loadSource(src);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setInitialLoading(false);

          const levels = hls.levels.map(
            (level: { height: number; bitrate: number }, index: number) => ({
              height: level.height || 0,
              bitrate: level.bitrate || 0,
              index,
            }),
          );

          if (levels.length > 1) {
            setQualityLevels(levels);
          }
        });

        hls.on(Hls.Events.LEVEL_SWITCHING, () => {
          setBuffering(true);
        });

        hls.on(Hls.Events.LEVEL_SWITCHED, () => {
          setBuffering(false);
        });

        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                setError("Network error - please check your connection");
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                setError("Media error - attempting recovery");
                hls.recoverMediaError();
                break;
              default:
                setError("Failed to load video");
                hls.destroy();
                break;
            }
            setInitialLoading(false);
            setBuffering(false);
          }
        });
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Failed to initialize player",
        );
        setInitialLoading(false);
      }
    };

    void loadHls();

    return () => {
      cancelled = true;
      if (nativeMetadataHandler && video) {
        video.removeEventListener("loadedmetadata", nativeMetadataHandler);
      }
      if (nativeErrorHandler && video) {
        video.removeEventListener("error", nativeErrorHandler);
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src]);

  useEffect(() => {
    if (!autoPlayMuted) return;
    const video = videoRef.current;
    if (!video) return;

    let attempted = false;
    const attempt = () => {
      if (attempted) return;
      attempted = true;
      video.muted = true;
      void video.play().catch(() => {
        video.muted = false;
        handleAutoplayBlocked();
      });
    };

    video.addEventListener("loadeddata", attempt);
    if (video.readyState >= 2) attempt();

    return () => {
      video.removeEventListener("loadeddata", attempt);
    };
  }, [autoPlayMuted]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleVideoEvent = (event: Event) => {
      switch (event.type) {
        case "play":
          handlePlay();
          break;
        case "ended":
          handleEnded();
          break;
        case "timeupdate":
          handleTimeUpdate();
          break;
        case "waiting":
        case "seeking":
          setBuffering(true);
          break;
        case "playing":
        case "canplay":
        case "seeked":
          setBuffering(false);
          break;
      }
    };

    const events = [
      "play",
      "ended",
      "timeupdate",
      "waiting",
      "playing",
      "canplay",
      "seeking",
      "seeked",
    ];
    for (const eventName of events) {
      video.addEventListener(eventName, handleVideoEvent);
    }
    return () => {
      for (const eventName of events) {
        video.removeEventListener(eventName, handleVideoEvent);
      }
    };
  }, []);

  if (!src) {
    return (
      <div
        className={cn(
          "bg-muted text-muted-foreground flex aspect-video w-full items-center justify-center rounded-lg",
          className,
        )}
      >
        <div className="flex flex-col items-center gap-2">
          <PlayCircle size={48} />
          <p>No video selected</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          "bg-muted text-muted-foreground flex aspect-video w-full items-center justify-center rounded-lg border-2 border-red-200",
          className,
        )}
      >
        <div className="flex flex-col items-center gap-2 p-4 text-center">
          <PlayCircle size={48} className="text-red-400" />
          <p className="font-medium text-red-400">Error loading video</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative aspect-video w-full overflow-hidden rounded-lg bg-black",
        className,
      )}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
          <Loader2 size={48} className="text-brand-red animate-spin" />
        </div>
      )}
      <VideoPlayer
        className="h-full w-full"
        noHotkeys={noHotkeys}
        autohide={autoHide !== undefined ? String(autoHide) : undefined}
      >
        <video
          ref={videoRef}
          slot="media"
          className="h-full w-full"
          playsInline
          autoPlay={autoPlayMuted}
          muted={autoPlayMuted}
          poster={poster}
          tabIndex={-1}
          suppressHydrationWarning
        />
        <VideoPlayerControlBar>
          <VideoPlayerPlayButton />
          <VideoPlayerSeekBackwardButton className="hidden sm:inline-flex" />
          <VideoPlayerSeekForwardButton className="hidden sm:inline-flex" />
          <VideoPlayerTimeDisplay showDuration noToggle />
          <VideoPlayerTimeRange />
          <VideoPlayerMuteButton />
          <VideoPlayerVolumeRange className="hidden sm:inline-flex" />
          <VideoPlayerSettings
            playbackRate={playbackRate}
            onPlaybackRateChange={handlePlaybackRateChange}
            qualityLevels={qualityLevels}
            currentQuality={currentQuality}
            onQualityChange={handleQualityChange}
          />
          <VideoPlayerFullscreenButton />
        </VideoPlayerControlBar>
      </VideoPlayer>
      {children}
    </div>
  );
};

export default HlsVideoPlayer;
